import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const healthAgents = [
  { label: 'Doc Parsing Agent', path: '/agents/doc-parsing', desc: 'Extract data from medical reports, insurance claims, and prescriptions.' },
  { label: 'KYC Onboarding', path: '/agents/kyc-onboarding', desc: 'Patient identity verification and secure onboarding workflows.' },
  { label: 'Email Assistant', path: '/agents/email-assistant', desc: 'Automate patient appointment requests and routine inquiries.' },
  { label: 'Data Engineer Agent', path: '/agents/data-engineering', desc: 'Governance for patient data platforms and medical record pipelines.' },
  { label: 'Governance Audit', path: '/agents/governance-audit', desc: 'Automate compliance checks for healthcare regulations and data privacy.' },
];

const faqs = [
  {
    q: 'How does Eagentix ensure patient data privacy?',
    a: 'Eagentix supports private cloud and on-premises deployment, ensuring patient data never leaves your secure environment. Agents are built with PII redaction and strict audit logging.',
  },
  {
    q: 'Can AI agents assist with healthcare compliance?',
    a: 'Yes. The Governance Audit Agent automates compliance checks against healthcare regulations, ensuring all AI-driven workflows maintain strict adherence to standards.',
  },
  {
    q: 'What healthcare workflows can be automated?',
    a: 'Claims processing, patient onboarding, medical document extraction, appointment scheduling, and clinical data engineering are prime candidates for AI agent automation.',
  },
];

export default function HealthcareAI() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI for Healthcare: Enterprise Agents | Eagentix"
        description="Eagentix provides enterprise AI agents for the healthcare sector — automating claims, patient onboarding, and medical document processing with strict governance."
        pathname="/industries/healthcare-ai-agents"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Industry · Healthcare
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Enterprise AI Agents for Healthcare
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Automate healthcare operations with purpose-built AI agents. From claims processing to patient data governance, Eagentix provides secure, compliant, and sovereign AI solutions.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Healthcare Solutions */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            AI Solutions for Healthcare Operations
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix agents handle the high-volume, repetitive tasks that slow down healthcare delivery.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {healthAgents.map((agent, i) => (
              <Link
                key={i}
                to={agent.path}
                style={{ ...card, textDecoration: 'none', display: 'block' }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0071e3', marginBottom: '8px' }}>{agent.label}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{agent.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Governance & Privacy */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Sovereign & Governed AI
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Healthcare AI requires more than just intelligence—it requires trust, security, and absolute data sovereignty.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              {
                title: 'Private Deployment',
                desc: 'Run agents on your own infrastructure or secure private cloud. Patient data never leaves your controlled environment.',
              },
              {
                title: 'PII Protection',
                desc: 'Automatic redaction and masking of Personally Identifiable Information (PII) before model processing.',
              },
              {
                title: 'Full Audit Trails',
                desc: 'Every decision and action taken by an agent is logged for compliance audits and clinical review.',
              },
            ].map((item, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                <p style={{ color: '#475569', lineHeight: 1.75, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Healthcare AI FAQ
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Modernise Healthcare Operations
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Explore how enterprise AI agents can transform your healthcare workflows with security and compliance at the core.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
